import { observer } from "mobx-react";
import React, {
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { injectStore } from "../../../stores/injectStore";
import { Store } from "../../../stores/Store";
import { DeprecationMessage } from "./message";
import { getPortalsForUser } from "./Portals/api/getPortalsForUser";
import {
  loadPortals,
  PortalContext,
  portalsLoaded,
  portalsLoadFailed,
  usePortalsReducer,
} from "./Portals/state";
import { asyncAction } from "./Portals/utils/asyncAction";
import { Portals } from "./Portals";
import { usePreloadingThumbnails } from "./Portals/hooks/usePreloadingThumbnails";

type Props = {
  ref?: RefObject<HTMLDivElement | null>;
  store: Store;
};

export function DeprecationMessagePage(props: Omit<Props, "store">) {
  const portalContextValue = usePortalsReducer();

  return (
    <PortalContext.Provider value={portalContextValue}>
      <DeprecationMessagePageContent ref={props.ref} />
    </PortalContext.Provider>
  );
}

const DeprecationMessagePageContentUnwrapped = observer(
  (props: Props): React.JSX.Element => {
    const [requestedPage, setRequestedPage] = useState<"message" | "portals">(
      "message",
    );

    const ref = useRef<HTMLDivElement>(null);
    const arePortalsReady = usePortalPreloading();

    const activePage =
      arePortalsReady && requestedPage === "portals" ? "portals" : "message";

    const goToPortals = useCallback(async () => {
      setRequestedPage("portals");
    }, []);

    let page: React.JSX.Element;

    switch (activePage) {
      case "message":
        page = (
          <DeprecationMessage
            goToPortals={goToPortals}
            arePortalsReady={arePortalsReady}
          />
        );
        break;

      case "portals":
        page = <Portals />;
        break;
    }

    return (
      <main ref={props.ref}>
        <TransitionGroup component={null}>
          <CSSTransition timeout={200} key={activePage} nodeRef={ref}>
            <div className="transition-content" ref={ref}>
              {page}
            </div>
          </CSSTransition>
        </TransitionGroup>

        <style jsx>{`
          main {
            margin: 83px 30px 60px;
          }

          .transition-content {
            &.enter {
              opacity: 0;

              &.enter-active {
                opacity: 1;
                transition: opacity 0.2s linear;
              }
            }

            &.exit {
              display: none;
            }
          }
        `}</style>
      </main>
    );
  },
);

const DeprecationMessagePageContent = injectStore(
  (store) => ({ store }),
  DeprecationMessagePageContentUnwrapped,
);

function usePortalPreloading() {
  const { dispatch, state } = useContext(PortalContext);

  const arePortalsReady = Array.isArray(state.portals);
  const areThumbnailsReady = usePreloadingThumbnails(
    state.portals instanceof Promise ? [] : state.portals ?? [],
  );

  useEffect(() => {
    // Start fetching data as soon as it has been requested.
    const portalPromise = getPortalsForUser();

    // ... and propagate it into our contexts using async actions
    asyncAction({
      dispatch: dispatch,
      promise: portalPromise,
      startAction: loadPortals,
      doneAction: portalsLoaded,
      failAction: portalsLoadFailed,
    });
  }, []);

  return arePortalsReady && areThumbnailsReady;
}
